import { useState, useEffect, useCallback, useRef } from 'react';
import type { Spot, SpotCategory } from '@ridehub/shared';
import { apiClient } from '../../lib/api-client.js';

interface MapBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface MapSpotsOptions {
  bounds: MapBounds | null;
  category?: SpotCategory;
}

export function useMapSpots({ bounds, category }: MapSpotsOptions) {
  const [spots, setSpots] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSpots = useCallback(async () => {
    if (!bounds) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        minLat: bounds.minLat.toString(),
        maxLat: bounds.maxLat.toString(),
        minLng: bounds.minLng.toString(),
        maxLng: bounds.maxLng.toString(),
        ...(category && { category }),
      });
      const data = await apiClient.get<Spot[]>(`/spots?${params}`, { signal: controller.signal });
      setSpots(data ?? []);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError('スポットの取得に失敗しました');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [bounds?.minLat, bounds?.maxLat, bounds?.minLng, bounds?.maxLng, category]);

  useEffect(() => {
    fetchSpots();
    return () => abortControllerRef.current?.abort();
  }, [fetchSpots]);

  return { spots, loading, error, refetch: fetchSpots };
}
